import React, {useCallback, useEffect, useState} from 'react';

import {AddToFolderPopover} from 'app/folders/components/AddToFolderPopover';
import {DeleteDocumentModal} from 'app/general/modals/DeleteDocumentModal';
import {
  ActionButtonBuilderType,
  ActionsButton,
} from 'app/general/widgets/ActionsButton';
import {AddToKnowledgeBasePopover} from 'app/kbs/components/AddToKnowledgeBasePopover';
import {gt} from 'app/utils/i18n';

import {BaseDocumentContentType, DocumentSerializedType} from 'sweet-types';

interface Props {
  children?: React.ReactElement | ActionButtonBuilderType;
  documents: Array<DocumentSerializedType>;
  currentFolder?: void | string;
  refreshData?: () => unknown;
  enableBulkSelect: boolean;
}

/* a button which opens a dropdown menu with avilable actions for the current document */
export function DocumentBulkActionsButton({
  children,
  documents,
  currentFolder,
  refreshData,
  enableBulkSelect,
}: Props): JSX.Element | null {
  if (!enableBulkSelect) {
    return null;
  }
  const [isShowing, setShow] = useState<
    null | 'knowledge-base' | 'delete' | 'folders'
  >(null);

  const close = () => setShow(null);

  const [currButtonRef, setCurrButtonRef] = useState<null | HTMLElement>(null);

  const setButtonRef = useCallback((newButtonRef) => {
    if (newButtonRef != currButtonRef) {
      setCurrButtonRef(newButtonRef);
    }
  }, []);

  const availableActions = React.useMemo(() => {
    return [
      {
        icon: 'far fa-folder',
        text: gt('Folders (%s selected)', documents.length),
        key: 'folders',
        disabled:
          !documents.every(
            (doc) =>
              ['procedure', 'policy', 'proceduregroup'].includes(
                doc.document_content_type
              ) && doc.can_edit
          ) || documents.length === 0,
        callback: () => setShow('folders'),
        condition: () => true,
      },
      {
        icon: 'far fa-book-open',
        text: gt('Knowledge Base (%s selected)', documents.length),
        key: 'knowledge-base',
        disabled:
          !documents.every(
            (doc) =>
              ['procedure', 'policy'].includes(doc.document_content_type) &&
              doc.can_edit
          ) || documents.length === 0,
        callback: () => setShow('knowledge-base'),
        condition: () => true,
      },
      {
        icon: 'far fa-trash',
        text: gt('Move to Recycle Bin (%s selected)', documents.length),
        key: 'recycle',
        disabled:
          !documents.every((doc) => doc.can_delete) || documents.length === 0,
        callback: () => setShow('delete'),
        condition: () => true,
      },
    ];
  }, [documents]);

  useEffect(() => {
    if (documents.length === 0) {
      close();
    }
  }, [documents]);

  // these props are used on all popovers
  const popoversOverlayProps = {
    target: currButtonRef as HTMLElement,
    trigger: 'legacy',
    placement: 'bottom' as const,
    isOpen: true,
    toggle: close,
  };

  const promisedCallback = () =>
    Promise.resolve(refreshData != null ? refreshData() : null);

  return (
    <>
      <ActionsButton ref={setButtonRef} actions={availableActions}>
        {children}
      </ActionsButton>
      {isShowing == null || currButtonRef == null ? null : isShowing ===
        'delete' ? (
        <DeleteDocumentModal
          show={true}
          documents={documents}
          redirectContentType={'document'}
          onClose={close}
          onDelete={promisedCallback}
        />
      ) : isShowing === 'knowledge-base' ? (
        <AddToKnowledgeBasePopover
          overlayProps={{...popoversOverlayProps, trigger: 'click'}}
          documents={documents.map((doc) => ({
            object_id: doc.object_id,
            document_content_type:
              doc.document_content_type as BaseDocumentContentType,
            url: doc.document.url,
          }))}
          onSuccess={promisedCallback}
        />
      ) : isShowing === 'folders' ? (
        <AddToFolderPopover
          overlayProps={{...popoversOverlayProps, trigger: 'click'}}
          onSuccess={promisedCallback}
          documents={documents.map((doc) => ({
            object_id: doc.object_id,
            document_content_type:
              doc.document_content_type as BaseDocumentContentType,
            url: doc.document.url,
          }))}
          currentFolder={currentFolder}
        />
      ) : null}
    </>
  );
}
